import React from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import moment from 'moment-timezone';

import { formatOverflow, formatDate, formatNull, formatCaseStatus } from '../../../Utils/GlobalUtils';
import { useModal } from '../../Modal/ModalContext';

import Modal from "../../Modal";
import DismissibleBanner from '../../DismissibleBanner';
import FilterAllCases from '../../FilterComponents/FilterAllCases';

import SearchCases from '../../SearchModals/SearchCases';
import WarningPanel from '../../WarningPanel';

import headerPanel from '../../../styles/HeaderPanel.module.css';

import LoadingLines from '../../LoadingLines';
import Loading from '../../Loading';

import IconContainer from '../../IconContainer';
import { ReactComponent as ReloadIcon } from '../../../icons/reload.svg';
import { ReactComponent as FunnelIcon } from '../../../icons/funnel-bold.svg';
import { ReactComponent as FunnelFillIcon } from '../../../icons/funnel-fill.svg';
import { ReactComponent as ShieldCheckIcon } from '../../../icons/shield-check-n.svg';
import { ReactComponent as ShieldWarningIcon } from '../../../icons/shield-warning.svg';

export default function AllProcedures() {
    const { openModal } = useModal();
    const location = useLocation();
    const navigate = useNavigate();

    const [cases, setCases] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [warning, setWarning] = React.useState('');
    const [reload, setReload] = React.useState(true);
    
    const [limitForView, setLimitForView] = React.useState(50);
    const [lastUpdateTime, setLastUpdateTime] = React.useState('--:--');

    const DEFAULT_FILTERS = {
        numbers: [],
        debtors: [],
        category: '',

        status_am: '',
        ams: [],
        mailing_status: '',
        date_mailing_from: '',
        date_mailing_to: '',
        date_court_from: '',
        date_court_to: '',
        date_am_appointment_r_from: '',
        date_am_appointment_r_to: '',

        status_am_appointment_approval: '',
        
        status: '',
    };

    const STORAGE_KEY = 'allcasesworker_filters';

    const [filters, setFilters] = React.useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            return saved ? { ...DEFAULT_FILTERS, ...JSON.parse(saved) } : DEFAULT_FILTERS;
        } catch {
            return DEFAULT_FILTERS;
        }
    });

    React.useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    }, [filters]);

    const hasActiveFilters = Object.entries(filters).some(([key, value]) => 
        Array.isArray(value) ? value.length > 0 : Boolean(value)
    );

    React.useEffect(() => {
        const token = localStorage.getItem('token');
        setLoading(true);

        if (!hasActiveFilters) {
            axios.get(`/api/get-all-cases?limitForView=${limitForView}`, {
                headers: { 
                    Authorization: `Bearer ${token}` 
                }
            }).then((response) => {
                setWarning('');
                setCases(response.data);
            })
            .catch((error) => {
                console.error(error);
                setWarning(`${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`);
            })
            .finally(() => {
                setReload(false);
                setLoading(false);
                setLastUpdateTime(new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }));
            });
        } else {
            axios.post('/api/get-all-cases-filtered', filters, {
                headers: { 
                    Authorization: `Bearer ${token}` 
                },
                params: { 
                    limitForView 
                },
            })
            .then((response) => {
                setWarning('');
                setCases(response.data);
            })
            .catch((error) => {
                console.error(error);
                setWarning(`${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`);
            })
            .finally(() => {
                setReload(false);
                setLoading(false);
                setLastUpdateTime(new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }));
            });
        }
    }, [reload]);

    const isRedDateAmAppointment = (caseItem) => {
        return (
            caseItem.date_court &&
            (!caseItem.date_am_appointment_r || !caseItem.date_am_appointment_m) &&
            caseItem.status === 'В работе' &&
            moment.tz(caseItem.date_court, 'Europe/Moscow').isBefore(moment.tz('Europe/Moscow'))
        );
    };

    const [isSearch, setIsSearch] = React.useState(false);

    React.useEffect(() => {
        if (window.location.hash === '#search') {
            setIsSearch(true);
        }
    }, []);

    const [isFilter, setIsFilter] = React.useState(false);
    const [fieldFilter, setFieldFilter] = React.useState(null);

    React.useEffect(() => {
        if (window.location.hash === '#filter') {
            setIsFilter(true);
        }
    }, []);

    return (
        <>
            <Modal isOpen={isSearch} closeModal={() => { window.location.hash = ''; setIsSearch(false) }}>
                <SearchCases />
            </Modal>
            <Modal isOpen={isFilter} closeModal={() => { setIsFilter(false); setFieldFilter('all'); setReload(true) }}>
                <FilterAllCases field={fieldFilter} filters={filters} setFilters={setFilters} DEFAULT_FILTERS={DEFAULT_FILTERS} />
            </Modal>

            <div className={headerPanel.panel}>
                <button
                    type='button' 
                    className='button' 
                    onClick={() => navigate(`/add-new-case`, { state: { from: location.pathname + location.search + location.hash }})}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M19,11H13V5a1,1,0,0,0-2,0v6H5a1,1,0,0,0,0,2h6v6a1,1,0,0,0,2,0V13h6a1,1,0,0,0,0-2Z" /></svg>
                    Добавить входящую корреспонденцию
                </button>
                <div className={headerPanel.searchBtn} onClick={() => { window.location.hash = 'search'; setIsSearch(true) }}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M21.71,20.29,18,16.61A9,9,0,1,0,16.61,18l3.68,3.68a1,1,0,0,0,1.42,0A1,1,0,0,0,21.71,20.29ZM11,18a7,7,0,1,1,7-7A7,7,0,0,1,11,18Z" /></svg>
                    Поиск...
                </div>
            </div>

            {/* <DismissibleBanner 
                id="new-feauture-filters-for-procedures-worker-page" 
                headline='Обновление логики работы карточек и новые функции'
                text={
                    <span
                        style={{display: 'flex', flexDirection: 'column', alignItems: 'start', gap: '3px'}}
                    >
                        <h3 style={{fontSize: '15px', fontWeight: '500'}}>Логика карточек дел изменена</h3>
                        Поле «Конечная дата исполнения» убрано. Вместо него добавлены новые поля, которые делают процесс понятнее и для управляющих, и для аппарата СРО.

                        <h3 style={{fontSize: '15px', fontWeight: '500'}}>Фильтры в Процедурах</h3>
                        Наведите курсор на шапку таблицы — появятся кнопки фильтрации. Фильтры теперь не сбрасываются после выхода со страницы.

                        <h3 style={{fontSize: '15px', fontWeight: '500'}}>Подтверждение назначения</h3>
                        Как только управляющий прикреплён к карточке, в действиях появляется кнопка «Подтвердить назначение». При подтверждении с управляющего автоматически списывается сумма за назначение.

                        <h3 style={{fontSize: '15px', fontWeight: '500'}}>Несколько должников и заявителей</h3>
                        Теперь к делу можно прикрепить сразу нескольких должников. Также добавлено новое поле — заявители, с той же возможностью множественного выбора.
                    </span>
                }
            /> */}
            <div className='pageWrapper'>
                <div className='tableContainer'>
                    <div className='tableHeaderWrapper'>
                        <div className='tableStatus'>
                            <div>
                                {hasActiveFilters && 
                                <span className='horizontalContainer'>
                                    <p>Применены фильтры</p>
                                    <p 
                                        className='aWithLink'
                                        onClick={() => {setFilters(DEFAULT_FILTERS); setReload(true)}}
                                    >
                                        Сбросить
                                    </p>
                                </span>
                                }
                            </div>
                            <a className='lastUpdateWrapper'>
                                Последнее обновление таблицы: {lastUpdateTime || '--:--'} 
                                {loading ? 
                                    <Loading type='inline' /> 
                                : 
                                    <IconContainer Icon={ReloadIcon} onClick={() => setReload(true)} size='14' color='black' />
                                }
                            </a>
                        </div>
                        <div className='tableHeader' style={{ gridTemplateColumns: '8fr 10fr 22fr 16fr 16fr 14fr 10fr' }}>
                            <div>
                                <p>Номер</p>
                                <IconContainer 
                                    Icon={filters.numbers.length > 0 ? FunnelFillIcon : FunnelIcon} 
                                    onClick={() => {
                                        setIsFilter(true);
                                        setFieldFilter('numbers');
                                    }} 
                                    size = '15' 
                                    color = 'black' 
                                    className = {`
                                        ${'filterHeaderIcon'} 
                                        ${filters.numbers.length > 0 && 'showedFilterHeaderIcon'}
                                    `}
                                />
                            </div>
                            <div>
                                <p>Должники</p>
                                <IconContainer 
                                    Icon={(filters.debtors.length > 0 || filters.category) ? FunnelFillIcon : FunnelIcon} 
                                    onClick={() => {
                                        setIsFilter(true);
                                        setFieldFilter('debtors');
                                    }} 
                                    size = '15' 
                                    color = 'black' 
                                    className = {`
                                        ${'filterHeaderIcon'} 
                                        ${(filters.debtors.length > 0 || filters.category) && 'showedFilterHeaderIcon'}
                                    `}
                                />
                            </div>
                            <div>
                                <p>Арбитражный суд / Управляющий</p>
                                <IconContainer 
                                    Icon={(filters.ams.length > 0 || filters.status_am) ? FunnelFillIcon : FunnelIcon} 
                                    onClick={() => {
                                        setIsFilter(true);
                                        setFieldFilter('ams');
                                    }} 
                                    size = '15' 
                                    color = 'black' 
                                    className = {`
                                        ${'filterHeaderIcon'} 
                                        ${(filters.ams.length > 0 || filters.status_am) && 'showedFilterHeaderIcon'}
                                    `}
                                />
                            </div>
                            <div>
                                <p>Дата судебного заседания об утверждении АУ</p>
                                <IconContainer 
                                    Icon={filters.date_court_from.length > 0? FunnelFillIcon : FunnelIcon} 
                                    onClick={() => {
                                        setIsFilter(true);
                                        setFieldFilter('date_court');
                                    }} 
                                    size = '15' 
                                    color = 'black' 
                                    className = {`
                                        ${'filterHeaderIcon'} 
                                        ${filters.date_court_from.length > 0 && 'showedFilterHeaderIcon'}
                                    `}
                                />
                            </div>
                            <div>
                                <p>Дата утверждения управляющего АС</p>
                                <IconContainer 
                                    Icon={(filters.date_am_appointment_r_from || filters.status_am_appointment_approval) ? FunnelFillIcon : FunnelIcon} 
                                    onClick={() => {
                                        setIsFilter(true);
                                        setFieldFilter('date_am_appointment');
                                    }} 
                                    size = '15' 
                                    color = 'black' 
                                    className = {`
                                        ${'filterHeaderIcon'} 
                                        ${(filters.date_am_appointment_r_from || filters.status_am_appointment_approval) && 'showedFilterHeaderIcon'}
                                    `}
                                />
                            </div>
                            <div>Начальная дата</div>
                            <div>
                                <p>Статус</p>
                                <IconContainer 
                                    Icon={(filters.status.length > 0 || filters.mailing_status || filters.date_mailing_from || filters.date_mailing_to) ? FunnelFillIcon : FunnelIcon} 
                                    onClick={() => {
                                        setIsFilter(true);
                                        setFieldFilter('status');
                                    }} 
                                    size = '15' 
                                    color = 'black' 
                                    className = {`
                                        ${'filterHeaderIcon'} 
                                        ${(filters.status.length > 0 || filters.mailing_status || filters.date_mailing_from || filters.date_mailing_to) && 'showedFilterHeaderIcon'}
                                    `}
                                />
                            </div>
                        </div>
                    </div>

                    {(cases.length===0 && loading) ? <LoadingLines /> 
                    : warning ? <WarningPanel text={warning} inTable={true} buttonText='Обновить' buttonAction={() => setReload(true)} />
                    : cases.map((caseItem, index) => (
                        <motion.div
                            key={index}
                            initial={{ scale: '97%', opacity: 0 }}
                            animate={{ scale: '100%', opacity: 1 }}
                            exit={{ scale: '97%', opacity: 0 }}
                            transition={{ duration: 0.3 }}

                            className='tableRow'
                            style={{ gridTemplateColumns: '8fr 10fr 22fr 16fr 16fr 14fr 10fr' }}
                            onClick={() => { 
                                navigate(`/case/${caseItem.id}`, { state: { from: location.pathname + location.search + location.hash }})
                            }}
                        >
                            <div>
                                {formatNull(caseItem.number)} 
                                {caseItem.nameCategory === 'Жалоба' && '(Жалоба)'} 
                                <span className='grayText'>{caseItem.internal_number}</span>
                            </div>
                            <div
                                className='CellForHorizontalLine'
                            >
                                {caseItem.debtors ?
                                    <p>
                                        {formatOverflow(caseItem.debtors.map((e) => e.name).join(', '))}
                                    </p>
                                    :
                                    formatNull(caseItem.debtors)
                                }
                            </div>
                            <div>
                                <p>{formatNull(caseItem.court_name)}</p>
                                <span className='horizontalLine'></span>
                                <span
                                    className='horizontalContainer'
                                >
                                    {caseItem.am ? 
                                        <p>{caseItem.am.lastname} {caseItem.am.firstname} {caseItem.am.surname} {caseItem.am.email}</p> 
                                    : 
                                        <p className={(caseItem.status!=='Управляющий не назначен' && caseItem.status!=='Завершено' ) && 'colorCell red'} style={{width: 'fit-content'}}>Управляющий не выбран</p>
                                    }
                                    {caseItem.status_am_appointment_approval &&
                                    <IconContainer 
                                        Icon={caseItem.status_am_appointment_approval=='Подтверждено' ? ShieldCheckIcon : ShieldWarningIcon} 
                                        isClickable={false}
                                        size = '17' 
                                        color = {caseItem.status_am_appointment_approval=='Подтверждено' ? 'green' : 'red'}  
                                        className = '' 
                                    />}
                                </span>
                            </div>
                            <div>{formatDate(caseItem.date_court)}</div>
                            <div className={isRedDateAmAppointment(caseItem) ? 'colorCell red' : ''}>
                                <p>{formatDate(caseItem.date_am_appointment_m)}</p>
                                <p className='description'>(полная версия)</p>
                                <p>{formatDate(caseItem.date_am_appointment_r)}</p>
                                <p className='description'>(рез. часть)</p>
                            </div>
                            <div>
                                <p>{formatDate(caseItem.date_start)}</p>
                            </div>
                            <div>
                                {formatCaseStatus(caseItem.status, 'textPill')}
                                {(caseItem.mailing_status=='Письма не отправлены' && caseItem.status=='Заявка') &&
                                <p
                                    className='colorCell red'
                                >
                                    Рассылка не совершена
                                </p>}
                            </div>
                        </motion.div>
                    ))}

                    {!warning && cases.length > 0 && (cases.length < cases[0].total_count) && (
                        <div className='tableLoadMoreRow' style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                            <div></div>
                            <div>
                                <button
                                    type='button'
                                    className='button'
                                    style={{ width: 'fit-content' }}
                                    onClick={() => {setLimitForView(prev => prev + 50); setReload(true)}}
                                    disabled={loading}
                                >
                                    {loading ? <Loading type='inline' /> : 'Загрузить ещё'}
                                </button>
                            </div>
                            <div></div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};
